import React, { useState, useEffect, useCallback } from 'react';
import { Table, Spin, Card, message, Select, Button, InputNumber, Space, Modal, Switch, Form, Input, Tag, Popconfirm } from 'antd';
import { ReloadOutlined, ThunderboltOutlined, CrownOutlined, StopOutlined, UnlockOutlined, CommentOutlined } from '@ant-design/icons';
import Cookies from 'js-cookie';
import { adminAPI } from '../api/admin';

const houses = [
  { label: 'تمام خانه‌ها', value: null },
  { label: 'Gryffindor', value: 'Gryffindor' },
  { label: 'Slytherin', value: 'Slytherin' },
  { label: 'Hufflepuff', value: 'Hufflepuff' },
  { label: 'Ravenclaw', value: 'Ravenclaw' }
];

const langNames = {
  fa: 'فارسی (fa)',
  en: 'انگلیسی (en)',
  ja: 'ژاپنی (ja)',
  de: 'آلمانی (de)',
  pt: 'پرتغالی (pt)',
  fr: 'فرانسوی (fr)',
  it: 'ایتالیایی (it)',
  es: 'اسپانیایی (es)',
  ko: 'کره‌ای (ko)'
};

export default function UsersList() {
  const adminCookie = (() => { try { return JSON.parse(Cookies.get('admin') || '{}'); } catch { return {}; } })();
  const isAdmin = (adminCookie.role || 'admin') === 'admin';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedHouse, setSelectedHouse] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });

  const loadUsers = useCallback(async (page = 1, house = null) => {
    setLoading(true);
    try {
      const filters = {};
      if (house) filters.house = house;

      const response = await adminAPI.getAllUsers(page, 10, filters);
      if (response.data.success) {
        setUsers(response.data.users);
        setPagination({
          page,
          limit: 10,
          total: response.data.pagination.total
        });
      }
    } catch (error) {
      message.error('خطا در بارگذاری کاربران');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleHouseChange = (house) => {
    setSelectedHouse(house);
    loadUsers(1, house);
  };

  const [attemptsModal, setAttemptsModal] = useState(null); // { user, value }

  const openAttemptsModal = (record) => {
    setAttemptsModal({ user: record, value: (record.platformMaxAttemptsOverride ?? 1) + 1 });
  };

  const submitAttempts = async () => {
    if (!attemptsModal) return;
    try {
      await adminAPI.setPlatformAttempts(attemptsModal.user._id, attemptsModal.value);
      message.success('سقف تلاش این کاربر برای عبور از سکو افزایش یافت');
      setAttemptsModal(null);
      loadUsers(pagination.page, selectedHouse);
    } catch {
      message.error('خطا در افزایش سقف تلاش');
    }
  };

  const toggleFreeTrainAccess = async (record, checked) => {
    try {
      await adminAPI.setFreeTrainAccess(record._id, checked);
      message.success(checked ? 'دسترسی آزاد به قطار فعال شد' : 'دسترسی آزاد به قطار غیرفعال شد');
      loadUsers(pagination.page, selectedHouse);
    } catch {
      message.error('خطا در تغییر دسترسی آزاد قطار');
    }
  };

  const [messageModal, setMessageModal] = useState(null); // user record
  const [msgForm] = Form.useForm();
  const [msgSubmitting, setMsgSubmitting] = useState(false);

  const openMessageModal = (record) => {
    setMessageModal(record);
    msgForm.resetFields();
  };

  const submitMessage = async (values) => {
    if (!messageModal) return;
    setMsgSubmitting(true);
    try {
      const res = await adminAPI.sendCustomMessageToUser(messageModal._id, {
        type: values.type,
        subject: values.subject,
        message: values.message,
        translate: !!values.translate
      });
      if (res.data.success) {
        message.success(res.data.message || 'پیام با موفقیت ارسال شد');
        setMessageModal(null);
      }
    } catch (error) {
      message.error(error?.response?.data?.message || 'خطا در ارسال پیام');
    } finally {
      setMsgSubmitting(false);
    }
  };

  // ─── محدود کردن دسترسی کاربر (بن دستی) ──────────────────────────────────
  const [banModal, setBanModal] = useState(null); // user record
  const [banReason, setBanReason] = useState('');
  const [banSubmitting, setBanSubmitting] = useState(false);

  const openBanModal = (record) => {
    setBanModal(record);
    setBanReason('');
  };

  const submitBan = async () => {
    if (!banModal) return;
    setBanSubmitting(true);
    try {
      await adminAPI.banUser(banModal._id, banReason.trim());
      message.success('دسترسی کاربر مسدود شد');
      setBanModal(null);
      loadUsers(pagination.page, selectedHouse);
    } catch (error) {
      message.error(error?.response?.data?.message || 'خطا در مسدود کردن کاربر');
    } finally {
      setBanSubmitting(false);
    }
  };

  const handleUnban = async (record) => {
    try {
      await adminAPI.unbanUser(record._id);
      message.success('دسترسی کاربر بازگردانده شد');
      loadUsers(pagination.page, selectedHouse);
    } catch (error) {
      message.error(error?.response?.data?.message || 'خطا در بازگردانی دسترسی');
    }
  };

  // ─── تبدیل دانش‌آموز به استاد ────────────────────────────────────────────
  // یک حساب Admin جدید (role='teacher') ساخته و به همین User لینک می‌شود.
  // حساب دانش‌آموزی این کاربر دست‌نخورده باقی می‌ماند.
  const [promoteModal, setPromoteModal] = useState(null); // user record
  const [promoteForm] = Form.useForm();
  const [promoting, setPromoting] = useState(false);

  const openPromoteModal = (record) => {
    setPromoteModal(record);
    promoteForm.resetFields();
    promoteForm.setFieldsValue({
      username: record.username || (record.phoneNumber || '').replace(/\D/g, ''),
      email: record.email || ''
    });
  };

  const submitPromote = async (values) => {
    if (!promoteModal) return;
    setPromoting(true);
    try {
      const res = await adminAPI.promoteUserToTeacher(promoteModal._id, values);
      if (res.data.success) {
        message.success(res.data.message || 'با موفقیت به استاد تبدیل شد');
        setPromoteModal(null);
        loadUsers(pagination.page, selectedHouse);
      }
    } catch (error) {
      message.error(error?.response?.data?.message || 'خطا در تبدیل به استاد');
    } finally {
      setPromoting(false);
    }
  };

  const columns = [
    {
      title: 'نام',
      render: (_, record) => `${record.firstName} ${record.lastName}`
    },
    {
      title: 'نام کاربری',
      dataIndex: 'username'
    },
    {
      title: 'شماره تماس',
      dataIndex: 'phoneNumber'
    },
    {
      title: 'معرف',
      render: (_, record) => record.referredBy ? `${record.referredBy.firstName} ${record.referredBy.lastName}` : '-'
    },
    {
      title: 'تعداد دعوت‌ها',
      render: (_, record) => record.referredUsers?.length || 0
    },
    {
      title: 'خانه',
      dataIndex: 'house',
      render: (house) => house || '-'
    },
    {
      title: 'جادوگر',
      render: (_, record) => {
        if (record.isWizard === null) return '⏳';
        return record.isWizard ? '✅' : '❌';
      }
    },
    {
      title: 'گالیون',
      dataIndex: 'galleons'
    },
    {
      title: 'تلاش‌های سکو',
      render: (_, record) => (
        <span>
          {record.platformTestAttempts || 0}
          {record.platformMaxAttemptsOverride != null ? ` / ${record.platformMaxAttemptsOverride}` : ''}
        </span>
      )
    },
    {
      title: 'تاریخ ثبت',
      render: (_, record) => new Date(record.createdAt).toLocaleDateString('fa-IR')
    },
    ...(isAdmin ? [{
      title: 'دسترسی آزاد قطار',
      render: (_, record) => (
        <Switch
          checked={!!record.freeTrainAccess}
          onChange={(checked) => toggleFreeTrainAccess(record, checked)}
          size="small"
        />
      )
    }] : []),
    ...(isAdmin ? [{
      title: 'استاد',
      render: (_, record) => (
        record.linkedAdminId
          ? <Tag color="blue" icon={<CrownOutlined />}>استاد شده</Tag>
          : <Button size="small" icon={<CrownOutlined />} onClick={() => openPromoteModal(record)}>تبدیل به استاد</Button>
      )
    }] : []),
    ...(isAdmin ? [{
      title: 'دسترسی',
      render: (_, record) => (
        record.banned
          ? <Tag color="red" title={record.banReason || ''}>مسدود</Tag>
          : <Tag color="green">آزاد</Tag>
      )
    }] : []),
    {
      title: '',
      render: (_, record) => (
        <Space size="small">
          <Button size="small" icon={<ThunderboltOutlined />} onClick={() => openAttemptsModal(record)}>
            تلاش سکو
          </Button>
          {isAdmin && (
            <Button size="small" icon={<CommentOutlined />} onClick={() => openMessageModal(record)}>
              ارسال پیام
            </Button>
          )}
        </Space>
      )
    },
    ...(isAdmin ? [{
      title: '',
      render: (_, record) => (
        record.banned
          ? (
            <Popconfirm
              title="رفع مسدودی این کاربر؟"
              okText="بله، آزاد کن"
              cancelText="انصراف"
              onConfirm={() => handleUnban(record)}
            >
              <Button size="small" icon={<UnlockOutlined />}>رفع مسدودی</Button>
            </Popconfirm>
          )
          : (
            <Button size="small" danger icon={<StopOutlined />} onClick={() => openBanModal(record)}>
              مسدود کردن
            </Button>
          )
      )
    }] : [])
  ];

  return (
    <Card title="مدیریت کاربران">
      <div style={{ marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        <Select
          value={selectedHouse}
          onChange={handleHouseChange}
          options={houses}
          style={{ width: '200px', maxWidth: '100%' }}
        />
        <Button icon={<ReloadOutlined />} onClick={() => loadUsers(1, selectedHouse)}>
          تازه‌سازی
        </Button>
      </div>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="_id"
          scroll={{ x: 'max-content' }}
          pagination={{
            pageSize: pagination.limit,
            total: pagination.total,
            current: pagination.page,
            onChange: (page) => loadUsers(page, selectedHouse)
          }}
        />
      </Spin>

      <Modal
        title={attemptsModal ? `افزایش سقف تلاش سکو — ${attemptsModal.user.firstName} ${attemptsModal.user.lastName}` : ''}
        open={!!attemptsModal}
        onCancel={() => setAttemptsModal(null)}
        onOk={submitAttempts}
        okText="ذخیره"
        cancelText="انصراف"
      >
        {attemptsModal && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ color: '#888' }}>
              تعداد تلاش‌های استفاده‌شده تا الان: {attemptsModal.user.platformTestAttempts || 0}
            </div>
            <div>سقف جدید تلاش برای این کاربر:</div>
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              value={attemptsModal.value}
              onChange={(v) => setAttemptsModal((m) => ({ ...m, value: v }))}
            />
          </Space>
        )}
      </Modal>

      <Modal
        title={banModal ? `مسدود کردن دسترسی — ${banModal.firstName} ${banModal.lastName}` : ''}
        open={!!banModal}
        onCancel={() => setBanModal(null)}
        onOk={submitBan}
        confirmLoading={banSubmitting}
        okText="مسدود کن"
        okButtonProps={{ danger: true }}
        cancelText="انصراف"
      >
        {banModal && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ color: '#888' }}>
              با تأیید این عملیات، این کاربر بلافاصله از هر فعالیتی در سایت (داشبورد، چت، سفر و ...)
              محروم می‌شود و پیامی برای او نمایش داده خواهد شد.
            </div>
            <div>دلیل مسدودی (اختیاری، به کاربر نمایش داده می‌شود):</div>
            <Input.TextArea
              rows={3}
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              placeholder="مثلاً: نقض قوانین گفتگو، ثبت‌نام تکراری و ..."
            />
          </Space>
        )}
      </Modal>

      <Modal
        title={promoteModal ? `تبدیل به استاد — ${promoteModal.firstName} ${promoteModal.lastName}` : ''}
        open={!!promoteModal}
        onCancel={() => setPromoteModal(null)}
        onOk={() => promoteForm.submit()}
        confirmLoading={promoting}
        okText="تبدیل به استاد"
        cancelText="انصراف"
      >
        <div style={{ color: '#888', marginBottom: 16 }}>
          یک حساب استاد جدید برای این شخص ساخته می‌شود. حساب دانش‌آموزی او (همین حساب) دست‌نخورده باقی می‌ماند
          و او می‌تواند هم با شماره تماس خودش به‌عنوان دانش‌آموز، و هم با نام کاربری/رمزی که اینجا تعیین می‌کنید
          به‌عنوان استاد از پنل ادمین وارد شود.
        </div>
        <Form form={promoteForm} layout="vertical" onFinish={submitPromote}>
          <Form.Item name="username" label="نام کاربری حساب استاد" rules={[{ required: true, message: 'نام کاربری الزامی است' }]}>
            <Input style={{ direction: 'ltr' }} />
          </Form.Item>
          <Form.Item name="password" label="رمز عبور حساب استاد" rules={[{ required: true, message: 'رمز عبور الزامی است' }, { min: 6, message: 'حداقل ۶ کاراکتر' }]}>
            <Input.Password style={{ direction: 'ltr' }} />
          </Form.Item>
          <Form.Item name="email" label="ایمیل (اختیاری)">
            <Input style={{ direction: 'ltr' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={messageModal ? `ارسال پیام مستقیم — ${messageModal.firstName} ${messageModal.lastName}` : ''}
        open={!!messageModal}
        onCancel={() => setMessageModal(null)}
        onOk={() => msgForm.submit()}
        confirmLoading={msgSubmitting}
        okText="ارسال پیام"
        cancelText="انصراف"
        width={600}
      >
        {messageModal && (
          <Form
            form={msgForm}
            layout="vertical"
            initialValues={{ type: 'sms', translate: true }}
            onFinish={submitMessage}
          >
            <div style={{ marginBottom: 16, background: '#f5f5f5', padding: 12, borderRadius: 8 }}>
              <div>شماره تماس: <strong>{messageModal.phoneNumber || '—'}</strong></div>
              {messageModal.email && <div>ایمیل: <strong>{messageModal.email}</strong></div>}
              <div>زبان ترجیحی کاربر: <Tag color="blue">{langNames[messageModal.language] || messageModal.language || 'فارسی (fa)'}</Tag></div>
            </div>

            <Form.Item
              name="type"
              label="روش ارسال"
              rules={[{ required: true }]}
            >
              <Select
                options={[
                  { label: 'پیامک (SMS)', value: 'sms' },
                  { label: 'ایمیل (Email)', value: 'email', disabled: !messageModal.email }
                ]}
              />
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.type !== curr.type}>
              {({ getFieldValue }) => getFieldValue('type') === 'email' ? (
                <Form.Item
                  name="subject"
                  label="موضوع ایمیل"
                  rules={[{ required: true, message: 'موضوع ایمیل الزامی است' }]}
                >
                  <Input placeholder="موضوع ایمیل را وارد کنید..." />
                </Form.Item>
              ) : null}
            </Form.Item>

            <Form.Item
              name="message"
              label="متن پیام (به زبان خودتان)"
              rules={[{ required: true, message: 'متن پیام الزامی است' }]}
            >
              <Input.TextArea rows={4} placeholder="متن پیام را بنویسید..." />
            </Form.Item>

            <Form.Item
              name="translate"
              valuePropName="checked"
            >
              <Switch checkedChildren="ترجمه هوشمند هوش مصنوعی فعال" unCheckedChildren="ارسال به صورت خام" />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </Card>
  );
}
